import React, { useId } from 'react';
import clsx from 'clsx';
import PropTypes from 'prop-types';

import { TextColor, TextVariant } from '../../constants/variants';
import Text from '../Text';

import * as s from './Select.module.scss';

const Select = React.memo(
  React.forwardRef(({ label, hint, error, options, placeholder, className, wrapperClassName, id, required, disabled, ...props }, ref) => {
    const generatedId = useId();
    const selectId = id || generatedId;
    const hintId = hint ? `${selectId}-hint` : undefined;
    const errorId = error ? `${selectId}-error` : undefined;

    return (
      <div className={clsx(s.wrapper, wrapperClassName)}>
        {label && (
          <label htmlFor={selectId} className={s.label}>
            {label}
            {required && (
              <span className={s.required} aria-hidden="true">
                {' '}
                *
              </span>
            )}
          </label>
        )}
        <div className={s.control}>
          <select
            ref={ref}
            id={selectId}
            disabled={disabled}
            aria-invalid={Boolean(error) || undefined}
            aria-describedby={[hintId, errorId].filter(Boolean).join(' ') || undefined}
            className={clsx(s.select, error && s.selectError, className)}
            {...props} // eslint-disable-line react/jsx-props-no-spreading
          >
            {placeholder && (
              <option value="" disabled>
                {placeholder}
              </option>
            )}
            {options.map((option) => (
              <option key={option.value} value={option.value} disabled={option.disabled}>
                {option.label}
              </option>
            ))}
          </select>
          <span className={s.chevron} aria-hidden="true" />
        </div>
        {hint && !error && (
          <Text as="span" id={hintId} variant={TextVariant.Caption} color={TextColor.Tertiary} className={s.hint}>
            {hint}
          </Text>
        )}
        {error && (
          <Text as="span" id={errorId} variant={TextVariant.Caption} color={TextColor.Danger} className={s.error} role="alert">
            {error}
          </Text>
        )}
      </div>
    );
  }),
);

Select.propTypes = {
  label: PropTypes.string,
  hint: PropTypes.string,
  error: PropTypes.string,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      label: PropTypes.string.isRequired,
      disabled: PropTypes.bool,
    }),
  ).isRequired,
  placeholder: PropTypes.string,
  className: PropTypes.string,
  wrapperClassName: PropTypes.string,
  id: PropTypes.string,
  required: PropTypes.bool,
  disabled: PropTypes.bool,
};

Select.defaultProps = {
  label: undefined,
  hint: undefined,
  error: undefined,
  placeholder: undefined,
  className: undefined,
  wrapperClassName: undefined,
  id: undefined,
  required: false,
  disabled: false,
};

export default Select;
